import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Disclosure,
  List,
  Stack,
  Strong,
  Text,
  TextLink,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import { useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { SITE } from 'src/app/shared/constants/configObjectProperties';
import {
  ALERT_VIEWED,
  CONTENT_ELEMENT_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { getConfig } from 'src/app/utils/config';
import { isSiteAnz } from 'src/app/utils/domain';
import { getPhoneNumber } from 'src/app/utils/getPhoneNumber';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import { useResendEmail } from 'src/app/v1/views/hooks/useResendEmail';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';

const ANALYTICS_OBJECTINTERACTION = 'resend-verification-email';

const ConfirmEmailAlert = () => {
  const { userEmailAddress } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.advertiserId,
      userEmailAddress: state.session.userEmailAddress,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);
  const showToast = useToast();
  const { resendEmail } = useResendEmail();

  const handleEmailNotFound = (expanded: boolean) => {
    if (expanded) {
      const eventData = {
        eventName: CONTENT_ELEMENT_PRESSED,
        elementText: "Can't find the email?",
        elementDesign: 'toggle',
        elementAction: 'expand',
        objectInteraction: ANALYTICS_OBJECTINTERACTION,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    }
  };

  const { createContactUsUrl } = useLocalisedRoutes();
  const site = getConfig(SITE);

  const contactUsUrl = createContactUsUrl();
  const isAnz = isSiteAnz(site);

  useEffect(() => {
    const eventData = {
      eventName: ALERT_VIEWED,
      alertShown: {
        type: 'banner',
        tone: 'cautionary',
        position: 'header',
        title: 'Verify your Email to complete registration',
        message:
          'Please finish creating your account by clicking on the confirmation link sent to [REDACTED]',
      },
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, [userEmailAddress]);

  const phoneNumber = getPhoneNumber(site);

  const handleResendEmail = () => {
    const eventData = {
      eventName: CONTENT_ELEMENT_PRESSED,
      elementText: 'Resend the confirmation email',
      elementDesign: 'link',
      elementAction: 'resend confirmation email',
      objectInteraction: ANALYTICS_OBJECTINTERACTION,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
    resendEmail();
    showToast({
      message: t('confirm-email-alert-success-message'),
      tone: 'positive',
    });
  };

  const CustomerServiceForAnz = () => (
    <Text>
      {t('confirm-email-alert-contact-label-anz', {
        CustomerServiceNumber: () => (
          <TextLink href={`tel:${phoneNumber}`}>{phoneNumber}</TextLink>
        ),
      })}
    </Text>
  );

  const CustomerServiceForAsia = () => (
    <Text>
      {t('confirm-email-alert-contact-label-asia', {
        Link: (children: React.ReactNode[]) => (
          <TextLink
            href={contactUsUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            {children}
          </TextLink>
        ),
      })}
    </Text>
  );

  return (
    <Alert id="confirm-email-alert" tone="caution">
      <Stack space="medium">
        <Text>
          <Strong>{t('confirm-email-alert-heading-label')}</Strong>
        </Text>
        <Text>
          {t('confirm-email-alert-message', {
            ConfirmEmailAddress: () => <Strong>{userEmailAddress}</Strong>,
          })}
        </Text>

        <Disclosure
          id="resendEmail"
          expandLabel={t('confirm-email-alert-expand-label')}
          space="small"
          onToggle={handleEmailNotFound}
        >
          <Box paddingTop="small">
            <List type="number">
              <Text>{t('confirm-email-alert-disclosure-label')}</Text>
              <Text>
                <TextLinkButton onClick={handleResendEmail}>
                  {t('confirm-email-alert-button-label')}
                </TextLinkButton>
              </Text>
              {isAnz ? <CustomerServiceForAnz /> : <CustomerServiceForAsia />}
            </List>
          </Box>
        </Disclosure>
      </Stack>
    </Alert>
  );
};

export default ConfirmEmailAlert;
